import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import type { WithSlotProps } from '~/components/hocs';
import type { DefineSlots } from '~/components/layout/utils';
import type { CommentThreadNode } from '~/store/comments/utils';
import { ErrorBoundary } from '~/components/common';
import { analytics } from '~/components/common/google-analytics';
import { withSlot } from '~/components/hocs';
import { CommentReplyForm } from '~/components/layout/comment-reply-form';
import { CommentsLoadMore } from '~/components/layout/comments-load-more';
import { componentRegistry, componentSlots, renderChild } from '~/components/layout/utils';
import { SlotsProvider } from '~/components/providers';
import { REPORT_OPTIONS } from '~/constants/common';
import { useTemplate } from '~/hooks';
import { useGetComments } from '~/hooks/api/use-get-comments';
import { selectCommentThread, selectVcmId, useStore } from '~/store';
import { selectActiveCommentId, setActiveCommentId } from '~/store/activeComment';
import { setModal } from '~/store/modal';
import { cn } from '~/utils/cn';
import { isTempCommentId } from '~/utils/comment-id';
import { getTimeSince } from '~/utils/get-time-since';

export const NAME = 'CommentThread';

export const Slots = componentSlots(NAME, {
  THREAD: 'thread',
  NAME: 'name',
  AVATAR_NAME: 'avatarName',
  TIME: 'time',
  MESSAGE: 'message',
  PATH: 'path',
  REPLIES: 'replies',
  REPLY_BTN: 'replyBtn',
  REPORT_BTN: 'reportBtn',
});

type SlotProps = DefineSlots<
  typeof Slots,
  {
    [Slots.THREAD]: JSX.IntrinsicElements['div'];
    [Slots.NAME]: JSX.IntrinsicElements['div'];
    [Slots.AVATAR_NAME]: JSX.IntrinsicElements['span'];
    [Slots.TIME]: JSX.IntrinsicElements['span'];
    [Slots.MESSAGE]: JSX.IntrinsicElements['div'];
    [Slots.PATH]: JSX.IntrinsicElements['div'];
    [Slots.REPLIES]: JSX.IntrinsicElements['div'];
    [Slots.REPLY_BTN]: JSX.IntrinsicElements['button'];
    [Slots.REPORT_BTN]: JSX.IntrinsicElements['button'];
  }
>;

export interface Props extends React.PropsWithChildren, WithSlotProps {
  commentId: string;
  className?: string;
  level?: number;
  isLast?: boolean;
}

const CommentThreadComponent = (props: { commentId: string }) => {
  const { commentId } = props;
  const commentThread = useStore(state => selectCommentThread(state, commentId));
  const activeCommentId = useStore(selectActiveCommentId);
  const shouldRenderReplyForm = commentId === activeCommentId;

  if (!commentThread) {
    throw new Error(`Unable to get comment thread for commentId: ${commentId}`);
  }

  return (
    <CommentThreadComponentMemo
      {...props}
      commentThread={commentThread}
      shouldRenderReplyForm={shouldRenderReplyForm}
    />
  );
};

CommentThreadComponent.displayName = NAME;

type MemoProps = Props & {
  commentThread: CommentThreadNode;
  shouldRenderReplyForm: boolean;
};

const CommentThreadComponentMemo = React.memo(function Memo(props: MemoProps) {
  const { commentThread, shouldRenderReplyForm = false, level = 0, isLast = false } = props;
  const template = useTemplate(NAME);
  const vcmId = useStore(selectVcmId);
  const [initialStatus] = useState(commentThread.status);
  const [isHighlighting, setIsHighlighting] = useState(initialStatus === 'pending');
  const tempReplies = useMemo(
    () => commentThread.replies.filter(({ id }) => isTempCommentId(id)),
    [commentThread.replies]
  );

  const repliesTotal = commentThread.replies_count;
  const repliesShown = commentThread.replies.length;
  const repliesToLoad = repliesTotal - repliesShown + tempReplies.length;
  const shouldRenderLoadMore = repliesToLoad > 0;

  const { query } = useGetComments({
    parentId: commentThread.id,
    enabled: false,
  });

  const slots = useMemo<SlotProps>(() => {
    const createdAtDate = new Date(commentThread.created_at);
    const children = commentThread.replies
      .filter(({ id }) => !isTempCommentId(id))
      .map(reply => (
        <CommentThread key={reply.id} commentId={reply.id} level={level + 1} isLast={!reply.replies.length} />
      ));

    if (shouldRenderReplyForm) {
      children.unshift(
        <CommentReplyForm
          parentId={commentThread.id}
          isLast={isLast}
          key="comment-reply-form"
          onSubmit={() => {
            setActiveCommentId(null);
          }}
        />
      );
    }

    if (shouldRenderLoadMore) {
      children.push(
        <CommentsLoadMore
          amount={repliesToLoad}
          disabled={query.isFetching}
          isLast={tempReplies.length === 0}
          key="comments-load-more"
          onLoadMoreClick={async () => {
            const result = await query.fetchNextPage().catch(console.error);
            const commentEl = document.querySelector(
              `[data-id="${result ? result.data?.pages.at(-1)?.comments.at(0)?.id : ''}"]`
            );

            commentEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
        />
      );
    }

    children.push(
      ...tempReplies.map(reply => (
        <CommentThread key={reply.id} commentId={reply.id} level={level + 1} isLast={!reply.replies.length} />
      ))
    );

    return {
      [Slots.THREAD]: {
        'data-id': commentThread.id,
        className: cn(`level-${level + 1}`, isHighlighting && 'highlight'),
      },
      [Slots.NAME]: {
        children: commentThread.responder_name,
      },
      [Slots.AVATAR_NAME]: {
        children: commentThread.responder_name[0],
      },
      [Slots.TIME]: {
        children: getTimeSince(createdAtDate),
        dateTime: createdAtDate.toISOString().split('T')[0], // '2025-04-06'
      },
      [Slots.MESSAGE]: {
        children: commentThread.content,
      },
      [Slots.PATH]: {
        className: isLast && !shouldRenderReplyForm && !shouldRenderLoadMore ? 'hidden' : '',
      },
      [Slots.REPLIES]: {
        children,
      },
      [Slots.REPLY_BTN]: {
        className: level === 2 || commentThread.status === 'pending' ? 'hidden' : '',
        onClick: () => {
          analytics.gtag.event('comment_reply_click', { VCMID: vcmId, commentId: commentThread.id });
          setActiveCommentId(!shouldRenderReplyForm ? commentThread.id : null);
        },
      },
      [Slots.REPORT_BTN]: {
        onClick: () => {
          analytics.gtag.event('comment_report_click', { VCMID: vcmId, commentId: commentThread.id });
          setModal({
            id: 'ReportModal',
            data: {
              onSubmit: (option, ownText) => {
                const key = `option-${option}`;
                const optionHeb = REPORT_OPTIONS[key] ?? '';
                const ownTextStr = ownText ? `: ${ownText}` : '';

                analytics.gtag.event('comment_report_submit', {
                  VCMID: vcmId,
                  commentId: commentThread.id,
                  reportType: optionHeb + ownTextStr,
                });

                console.log('Report', commentThread);
                setModal(null);
              },
            },
          });
        },
      },
    };
  }, [
    query,
    commentThread,
    level,
    isLast,
    shouldRenderReplyForm,
    shouldRenderLoadMore,
    repliesToLoad,
    isHighlighting,
    tempReplies,
    vcmId,
  ]);

  useEffect(() => {
    if (initialStatus === 'pending') {
      setTimeout(() => {
        setIsHighlighting(false);
      }, 3_000);
    }
  }, [initialStatus]);

  return <SlotsProvider slots={slots}>{renderChild(template)}</SlotsProvider>;
});

export const ErrorSlots = componentSlots('CommentThreadError', {
  MESSAGE: 'message',
});

type ErrorSlotProps = DefineSlots<
  typeof ErrorSlots,
  {
    [ErrorSlots.MESSAGE]: JSX.IntrinsicElements['p'];
  }
>;

const CommentThreadError = (props: { error: Error }) => {
  const { error } = props;
  const template = useTemplate('CommentThreadError');
  const slots = useMemo<ErrorSlotProps>(
    () => ({
      [ErrorSlots.MESSAGE]: {
        children: error.message,
      },
    }),
    [error]
  );

  return <SlotsProvider slots={slots}>{renderChild(template)}</SlotsProvider>;
};

const CommentThreadErrorBoundaryComponent = (props: Props) => {
  return (
    <ErrorBoundary fallback={error => <CommentThreadError error={error} />}>
      <CommentThreadComponent {...props} />
    </ErrorBoundary>
  );
};

export const CommentThread = withSlot(CommentThreadErrorBoundaryComponent);
componentRegistry.add(NAME, CommentThread as React.FC);
